import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItemButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SearchIcon from '@mui/icons-material/Search';
import { apiFetch } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { addDays, formatShort, today as todayIso, type IsoDate } from '../lib/dates';

/**
 * Notes.
 *
 * A note is a body, a date range, and the crew it is for — which is what the
 * one genuine note in the legacy database was. Everything else in that module
 * was scaffolding: three tables, one of them never written to, and a
 * 2,019-line controller forked from the SMS one.
 *
 * The one thing the legacy version could not tell you is whether anybody had
 * read the note, which for "ladders required" is the only thing that matters.
 */

interface Recipient {
    readonly staffId: number;
    readonly staffName: string | null;
    readonly readAt: string | null;
}

interface Note {
    readonly id: number;
    readonly body: string;
    readonly startsOn: IsoDate;
    readonly endsOn: IsoDate;
    readonly createdByName: string | null;
    readonly recipients: readonly Recipient[];
    readonly readCount: number;
}

interface StaffSummary {
    readonly staffId: number;
    readonly name: string;
}

type Period = 'current' | 'upcoming' | 'past' | 'all';

/** In force today, upcoming, then finished — the order they matter in. */
const PERIOD_FILTERS: readonly { readonly value: Period; readonly label: string }[] = [
    { value: 'current', label: 'In Force Today' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'past', label: 'Finished' },
    { value: 'all', label: 'All' },
];

function periodOf(n: Note, today: IsoDate): Exclude<Period, 'all'> {
    if (n.startsOn <= today && n.endsOn >= today) {
        return 'current';
    }
    return n.startsOn > today ? 'upcoming' : 'past';
}

export function Notes(): React.JSX.Element {
    const today = useMemo(() => todayIso(), []);
    const [notes, setNotes] = useState<readonly Note[]>([]);
    const [staff, setStaff] = useState<readonly StaffSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);
    const [editing, setEditing] = useState<Note | 'new' | null>(null);
    const [period, setPeriod] = useState<Period>('current');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        apiFetch<{ staff: readonly StaffSummary[] }>('/staff')
            .then((r) => {
                setStaff(r.staff);
            })
            .catch(() => {
                /* The picker stays empty; the list still loads. */
            });
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        apiFetch<{ notes: readonly Note[] }>('/notes')
            .then((r) => {
                if (!cancelled) {
                    setNotes(r.notes);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load notes');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    function remove(id: number): void {
        apiFetch<unknown>(`/notes/${String(id)}`, { method: 'DELETE' })
            .then(reload)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not remove');
            });
    }

    const counts = useMemo(() => {
        const c: Record<Period, number> = { current: 0, upcoming: 0, past: 0, all: notes.length };
        for (const n of notes) {
            c[periodOf(n, today)] += 1;
        }
        return c;
    }, [notes, today]);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return notes.filter(
            (n) =>
                (period === 'all' || periodOf(n, today) === period) &&
                (q === '' ||
                    n.body.toLowerCase().includes(q) ||
                    n.recipients.some((r) => (r.staffName ?? '').toLowerCase().includes(q))),
        );
    }, [notes, period, search, today]);

    const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = rows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="Notes" subtitle="Instructions for named crew on named days" />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        flexWrap="wrap"
                        sx={{ flex: 1 }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search note or crew..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            slotProps={{
                                input: { startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> },
                            }}
                            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 360 } }}
                        />
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                            {PERIOD_FILTERS.map((f) => (
                                <Button
                                    key={f.value}
                                    size="small"
                                    variant={period === f.value ? 'contained' : 'outlined'}
                                    color={period === f.value ? 'primary' : 'inherit'}
                                    onClick={() => {
                                        setPeriod(f.value);
                                        setPage(0);
                                    }}
                                    sx={{ flex: { xs: '1 1 40%', sm: '0 0 auto' } }}
                                >
                                    {f.label}
                                    {loading ? '' : ` (${String(counts[f.value])})`}
                                </Button>
                            ))}
                            {loading ? <CircularProgress size={18} /> : null}
                        </Stack>
                    </Stack>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setEditing('new');
                        }}
                    >
                        New Note
                    </Button>
                </Stack>
            </Paper>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
                    {error}
                </Alert>
            ) : null}

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 820 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Note</TableCell>
                            <TableCell>Dates</TableCell>
                            <TableCell>Crew</TableCell>
                            <TableCell>Read</TableCell>
                            <TableCell>Created By</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary">
                                        {loading
                                            ? 'Loading notes...'
                                            : search.trim() === ''
                                              ? 'No notes here.'
                                              : `No notes match "${search}".`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageRows.map((n, idx) => (
                                <TableRow
                                    key={n.id}
                                    hover
                                    onClick={() => {
                                        setEditing(n);
                                    }}
                                    sx={{ cursor: 'pointer', bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}
                                >
                                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'pre-wrap', maxWidth: 420 }}>
                                        {n.body}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {n.startsOn === n.endsOn
                                            ? formatShort(n.startsOn)
                                            : `${formatShort(n.startsOn)} – ${formatShort(n.endsOn)}`}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                            {n.recipients.map((r) => (
                                                <Tooltip
                                                    key={r.staffId}
                                                    title={
                                                        r.readAt === null
                                                            ? 'Not opened yet'
                                                            : `Read ${formatShort(r.readAt.slice(0, 10))}`
                                                    }
                                                >
                                                    <Chip
                                                        size="small"
                                                        variant={r.readAt === null ? 'outlined' : 'filled'}
                                                        color={r.readAt === null ? 'default' : 'success'}
                                                        label={r.staffName ?? `#${String(r.staffId)}`}
                                                    />
                                                </Tooltip>
                                            ))}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {/* The legacy module had no way to answer this. */}
                                        <Tooltip title="How many have opened it">
                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                <DoneAllIcon
                                                    fontSize="small"
                                                    sx={{
                                                        color:
                                                            n.readCount === n.recipients.length
                                                                ? 'success.main'
                                                                : 'text.disabled',
                                                    }}
                                                />
                                                <span>
                                                    {n.readCount}/{n.recipients.length}
                                                </span>
                                            </Stack>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{n.createdByName ?? '—'}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditing(n);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    remove(n.id);
                                                }}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={rows.length}
                page={currentPage}
                onPageChange={(_e, p) => {
                    setPage(p);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(Number.parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ bgcolor: 'background.paper', borderRadius: 1, mt: 1 }}
            />

            <NoteDialog
                open={editing !== null}
                note={editing === 'new' ? null : editing}
                staff={staff}
                defaultDate={today}
                onClose={() => {
                    setEditing(null);
                }}
                onSaved={() => {
                    setEditing(null);
                    reload();
                }}
            />
        </Container>
    );
}

// ─── Dialog ─────────────────────────────────────────────────────────────────

interface NoteDialogProps {
    readonly open: boolean;
    readonly note: Note | null;
    readonly staff: readonly StaffSummary[];
    readonly defaultDate: IsoDate;
    readonly onClose: () => void;
    readonly onSaved: () => void;
}

function NoteDialog({ open, note, staff, defaultDate, onClose, onSaved }: NoteDialogProps): React.JSX.Element {
    const [body, setBody] = useState('');
    const [startsOn, setStartsOn] = useState('');
    const [endsOn, setEndsOn] = useState('');
    const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        setError(null);
        setSearch('');
        if (note === null) {
            setBody('');
            setStartsOn(defaultDate);
            setEndsOn(defaultDate);
            setSelected(new Set());
            return;
        }
        setBody(note.body);
        setStartsOn(note.startsOn);
        setEndsOn(note.endsOn);
        setSelected(new Set(note.recipients.map((r) => r.staffId)));
    }, [open, note, defaultDate]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q === '' ? staff : staff.filter((s) => s.name.toLowerCase().includes(q));
    }, [staff, search]);

    const datesValid = startsOn !== '' && endsOn !== '' && endsOn >= startsOn;

    function save(): void {
        setSaving(true);
        setError(null);
        const payload = { body, startsOn, endsOn, staffIds: [...selected] };
        const path = note === null ? '/notes' : `/notes/${String(note.id)}`;
        apiFetch<unknown>(path, { method: note === null ? 'POST' : 'PATCH', body: JSON.stringify(payload) })
            .then(onSaved)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 800 }}>{note === null ? 'New note' : 'Edit note'}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Note"
                        multiline
                        minRows={3}
                        fullWidth
                        autoFocus
                        value={body}
                        onChange={(e) => {
                            setBody(e.target.value);
                        }}
                    />

                    <Stack direction="row" gap={1.5} alignItems="flex-start">
                        <TextField
                            label="From"
                            type="date"
                            size="small"
                            fullWidth
                            value={startsOn}
                            onChange={(e) => {
                                setStartsOn(e.target.value);
                                // A note usually applies to one day; keeping the
                                // end in step avoids an invalid range by default.
                                if (endsOn === '' || endsOn < e.target.value) {
                                    setEndsOn(e.target.value);
                                }
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            label="To"
                            type="date"
                            size="small"
                            fullWidth
                            value={endsOn}
                            onChange={(e) => {
                                setEndsOn(e.target.value);
                            }}
                            error={!datesValid && endsOn !== ''}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <Button
                            size="small"
                            onClick={() => {
                                setEndsOn(addDays(startsOn, 6));
                            }}
                            sx={{ mt: 0.5, flex: '0 0 auto' }}
                        >
                            A week
                        </Button>
                    </Stack>

                    <Box>
                        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, flex: 1 }}>
                                Who sees it ({selected.size})
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => {
                                    setSelected(
                                        selected.size === filtered.length
                                            ? new Set()
                                            : new Set(filtered.map((s) => s.staffId)),
                                    );
                                }}
                            >
                                {selected.size === filtered.length && filtered.length > 0 ? 'None' : 'All'}
                            </Button>
                        </Stack>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search staff"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                            }}
                        />
                        <Divider sx={{ my: 1 }} />
                        <List dense sx={{ maxHeight: 220, overflow: 'auto' }}>
                            {filtered.map((s) => (
                                <ListItemButton
                                    key={s.staffId}
                                    onClick={() => {
                                        setSelected((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(s.staffId)) {
                                                next.delete(s.staffId);
                                            } else {
                                                next.add(s.staffId);
                                            }
                                            return next;
                                        });
                                    }}
                                    sx={{ borderRadius: 1, py: 0 }}
                                >
                                    <Checkbox
                                        edge="start"
                                        size="small"
                                        checked={selected.has(s.staffId)}
                                        tabIndex={-1}
                                    />
                                    <Typography noWrap sx={{ fontSize: '0.82rem' }}>
                                        {s.name}
                                    </Typography>
                                </ListItemButton>
                            ))}
                        </List>
                    </Box>

                    {error !== null ? <Alert severity="error">{error}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={saving || body.trim() === '' || selected.size === 0 || !datesValid}
                >
                    {note === null ? 'Send note' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
